import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign, TrendingUp, Users, MousePointerClick, Plus, Trash2,
  Download, Globe, BarChart3, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';



export default function RevenuePage() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [summary, setSummary] = useState(null);
  const [bySite, setBySite] = useState([]);
  const [byCountry, setByCountry] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [entries, setEntries] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newEntry, setNewEntry] = useState({
    site_id: '', period: new Date().toISOString().slice(0, 7), revenue_model: 'cpa',
    amount: '', clicks: '', registrations: '', deposits: '', country: 'TR', notes: '', currency: 'USD'
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const siteParam = selectedSite !== 'all' ? selectedSite : undefined;
      const [sitesRes, summaryRes, bySiteRes, byCountryRes, monthlyRes, entriesRes] = await Promise.all([
        api.get('/sites'),
        api.get('/revenue/summary', { params: { site_id: siteParam } }),
        api.get('/revenue/by-site'),
        api.get('/revenue/by-country', { params: { site_id: siteParam } }),
        api.get('/revenue/monthly', { params: { site_id: siteParam } }),
        api.get('/revenue/entries', { params: { site_id: siteParam } }),
      ]);
      setSites(sitesRes.data);
      setSummary(summaryRes.data);
      setBySite(bySiteRes.data);
      setByCountry(byCountryRes.data);
      setMonthly(monthlyRes.data);
      setEntries(entriesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedSite]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddEntry = async () => {
    try {
      await api.post('/revenue/entries', {
        ...newEntry,
        amount: parseFloat(newEntry.amount) || 0,
        clicks: parseInt(newEntry.clicks) || 0,
        registrations: parseInt(newEntry.registrations) || 0,
        deposits: parseInt(newEntry.deposits) || 0,
      });
      toast.success('Gelir kaydedildi');
      setShowAdd(false);
      setNewEntry({ site_id: '', period: new Date().toISOString().slice(0, 7), revenue_model: 'cpa', amount: '', clicks: '', registrations: '', deposits: '', country: 'TR', notes: '', currency: 'USD' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kaydedilemedi'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydi silmek istediginize emin misiniz?')) return;
    try { await api.delete(`/revenue/entries/${id}`); toast.success('Silindi'); loadData(); }
    catch (err) { toast.error('Silinemedi'); }
  };

  const exportCSV = () => {
    const token = localStorage.getItem('trafficos_token');
    const siteParam = selectedSite !== 'all' ? `&site_id=${selectedSite}` : '';
    window.open(`/api/reports/csv?report_type=revenue${siteParam}&token=${token}`, '_blank');
  };

  const getSiteName = (id) => sites.find(s => s.id === id)?.brand_name || id?.slice(0, 8);

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 rounded-xl shimmer" />)}</div>;

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Gelir Istihbarati</h1>
          <p className="text-sm text-muted-foreground mt-1">Affiliate gelir analizi & metrikleri</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-[220px] bg-secondary/40 border-border/70"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tum Siteler</SelectItem>
              {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.brand_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={exportCSV} variant="outline" className="gap-2 border-border/70"><Download className="w-4 h-4" /> CSV</Button>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] gap-2 press-active">
                <Plus className="w-4 h-4" /> Gelir Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="z-[100]">
              <DialogHeader><DialogTitle>Manuel Gelir Girisi</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div><Label className="text-xs text-muted-foreground">Site</Label>
                  <Select value={newEntry.site_id} onValueChange={(v) => setNewEntry({...newEntry, site_id: v})}>
                    <SelectTrigger className="mt-1 bg-secondary/40"><SelectValue placeholder="Site secin" /></SelectTrigger>
                    <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.brand_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-muted-foreground">Donem</Label>
                    <Input type="month" value={newEntry.period} onChange={(e) => setNewEntry({...newEntry, period: e.target.value})} className="mt-1 bg-secondary/40" /></div>
                  <div><Label className="text-xs text-muted-foreground">Model</Label>
                    <Select value={newEntry.revenue_model} onValueChange={(v) => setNewEntry({...newEntry, revenue_model: v})}>
                      <SelectTrigger className="mt-1 bg-secondary/40"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cpa">CPA</SelectItem><SelectItem value="revshare">RevShare</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-muted-foreground">Tutar ($)</Label>
                    <Input type="number" value={newEntry.amount} onChange={(e) => setNewEntry({...newEntry, amount: e.target.value})} className="mt-1 bg-secondary/40" placeholder="0.00" /></div>
                  <div><Label className="text-xs text-muted-foreground">Tiklamalar</Label>
                    <Input type="number" value={newEntry.clicks} onChange={(e) => setNewEntry({...newEntry, clicks: e.target.value})} className="mt-1 bg-secondary/40" placeholder="0" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs text-muted-foreground">Kayitlar</Label>
                    <Input type="number" value={newEntry.registrations} onChange={(e) => setNewEntry({...newEntry, registrations: e.target.value})} className="mt-1 bg-secondary/40" placeholder="0" /></div>
                  <div><Label className="text-xs text-muted-foreground">Yatirimlar</Label>
                    <Input type="number" value={newEntry.deposits} onChange={(e) => setNewEntry({...newEntry, deposits: e.target.value})} className="mt-1 bg-secondary/40" placeholder="0" /></div>
                  <div><Label className="text-xs text-muted-foreground">Ulke</Label>
                    <Input value={newEntry.country} onChange={(e) => setNewEntry({...newEntry, country: e.target.value})} className="mt-1 bg-secondary/40" placeholder="TR" /></div>
                </div>
                <Button onClick={handleAddEntry} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">Kaydet</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        {[
          { label: 'Toplam Gelir', value: `$${summary?.total_revenue || 0}`, icon: DollarSign, color: 'text-emerald-400' },
          { label: 'EPC', value: `$${summary?.epc || 0}`, icon: MousePointerClick, color: 'text-[hsl(var(--primary))]' },
          { label: 'RPM', value: `$${summary?.rpm || 0}`, icon: BarChart3, color: 'text-blue-400' },
          { label: 'Donusum Orani', value: `%${summary?.conversion_rate || 0}`, icon: TrendingUp, color: 'text-amber-400' },
          { label: 'Kayit Sayisi', value: summary?.total_registrations || 0, icon: Users, color: 'text-muted-foreground' },
        ].map((kpi, i) => (
          <Card key={i} className="relative border-border/70 overflow-hidden"><div className="kpi-accent" />
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
              <p className={`text-xl font-semibold mt-1 font-['Space_Grotesk'] ${kpi.color}`}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly Revenue Chart */}
      <Card className="border-border/70">
        <CardHeader className="pb-2"><CardTitle className="text-base">Aylik Gelir Trendi</CardTitle></CardHeader>
        <CardContent>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid stroke="rgba(167,176,190,0.1)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#A7B0BE', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#A7B0BE', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#12151A', border: '1px solid #262C35', borderRadius: 8, color: '#F2F4F7' }} />
                <Bar dataKey="revenue" fill="#D4AF37" radius={[4, 4, 0, 0]} name="Gelir ($)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* By Site */}
        <Card className="border-border/70">
          <CardHeader><CardTitle className="text-base">Site Bazli Gelir</CardTitle></CardHeader>
          <CardContent>
            {bySite.length === 0 ? <p className="text-sm text-muted-foreground">Henuz veri yok</p> : (
              <div className="space-y-2">{bySite.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/20">
                  <div><p className="text-sm font-medium">{s.brand_name || getSiteName(s.site_id)}</p><p className="text-[10px] text-muted-foreground">{s.domain}</p></div>
                  <div className="text-right"><p className="text-sm font-semibold text-emerald-400">${s.total_revenue}</p><p className="text-[10px] text-muted-foreground">EPC: ${s.epc}</p></div>
                </div>
              ))}</div>
            )}
          </CardContent>
        </Card>

        {/* By Country */}
        <Card className="border-border/70">
          <CardHeader><CardTitle className="text-base">Ulke Bazli Gelir</CardTitle></CardHeader>
          <CardContent>
            {byCountry.length === 0 ? <p className="text-sm text-muted-foreground">Henuz veri yok</p> : (
              <div className="space-y-2">{byCountry.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/20">
                  <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-muted-foreground" /><span className="text-sm font-medium">{c.country}</span></div>
                  <div className="text-right"><p className="text-sm font-semibold text-emerald-400">${c.total_revenue}</p><p className="text-[10px] text-muted-foreground">{c.total_registrations} kayit</p></div>
                </div>
              ))}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Entries */}
      <Card className="border-border/70">
        <CardHeader><CardTitle className="text-base">Son Gelir Kayitlari</CardTitle></CardHeader>
        <CardContent>
          {entries.length === 0 ? <p className="text-sm text-muted-foreground py-4">Henuz kayit yok. Gelir Ekle butonu ile baslayabilirsiniz.</p> : (
            <div className="space-y-2">{entries.slice(0, 10).map((e, i) => (
              <div key={e.id || i} className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/20">
                <div className="flex items-center gap-4">
                  <span className="text-xs font-mono text-muted-foreground">{e.period}</span>
                  <span className="text-sm">{getSiteName(e.site_id)}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/60 text-muted-foreground uppercase">{e.revenue_model}</span>
                  <span className="text-[10px] text-muted-foreground">{e.country}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-emerald-400">${e.amount}</span>
                  <span className="text-xs text-muted-foreground">{e.clicks}clk / {e.registrations}reg</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(e.id)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                </div>
              </div>
            ))}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
